/**
 * React hook for Impact Plexus Chatbot
 * Manages messages, streaming, session state
 */
import { useState, useCallback, useRef } from 'react';
import { streamChatMessage, sendChatMessage, clearChatSession } from '../services/chatbot';

export function useChatbot(charityBN) {
  const [messages, setMessages] = useState([
    {
      id: 'welcome',
      role: 'assistant',
      content: "Hello! I'm the Impact Plexus Copilot — your AI assistant for grant discovery. I can help you find grants, understand application requirements, and navigate the Canadian funding landscape. What would you like to explore?",
      timestamp: new Date(),
    },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [error, setError] = useState(null);
  const streamingRef = useRef('');

  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || isLoading) return;

    const userMsg = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: text.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMsg]);
    setIsLoading(true);
    setError(null);
    streamingRef.current = '';

    // Add placeholder for assistant response
    const assistantId = `assistant-${Date.now()}`;
    setMessages(prev => [...prev, {
      id: assistantId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      streaming: true,
    }]);

    try {
      await streamChatMessage({
        message: text.trim(),
        sessionId,
        charityBN,
        onChunk: (chunk, sid) => {
          if (sid && !sessionId) setSessionId(sid);
          streamingRef.current += chunk;
          setMessages(prev =>
            prev.map(m =>
              m.id === assistantId
                ? { ...m, content: streamingRef.current }
                : m
            )
          );
        },
        onDone: (sid) => {
          if (sid) setSessionId(sid);
          setMessages(prev =>
            prev.map(m =>
              m.id === assistantId
                ? { ...m, streaming: false }
                : m
            )
          );
          setIsLoading(false);
        },
        onError: (errMsg) => {
          // Fallback to non-streaming
          fallbackSend(text.trim(), assistantId);
        },
      });
    } catch {
      fallbackSend(text.trim(), assistantId);
    }
  }, [isLoading, sessionId, charityBN]);

  const fallbackSend = useCallback(async (text, assistantId) => {
    try {
      const data = await sendChatMessage({
        message: text,
        sessionId,
        charityBN,
      });
      if (data.session_id) setSessionId(data.session_id);
      setMessages(prev =>
        prev.map(m =>
          m.id === assistantId
            ? { ...m, content: data.reply, streaming: false }
            : m
        )
      );
    } catch (err) {
      setError(err.message || 'Failed to get response');
      setMessages(prev => prev.filter(m => m.id !== assistantId));
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, charityBN]);

  const clearChat = useCallback(async () => {
    if (sessionId) {
      try { await clearChatSession(sessionId); } catch {}
    }
    setSessionId(null);
    setMessages([{
      id: 'welcome',
      role: 'assistant',
      content: "Chat cleared. How can I help you with grants today?",
      timestamp: new Date(),
    }]);
    setError(null);
  }, [sessionId]);

  return { messages, isLoading, error, sendMessage, clearChat, sessionId };
}
